import { ApiError } from '@/api/client';

/**
 * Error codes in the operator's language (TZ sections 14 and 17).
 *
 * The backend answers with a stable code and an English sentence, because the
 * code is a contract and the sentence is for the logs. The interface is Russian,
 * so it renders the code it recognises and falls back to the server's own words
 * for anything it does not — an untranslated sentence is still information, and
 * silently swallowing it would leave the operator with nothing.
 */
const MESSAGES: Record<string, string> = {
  UNAUTHENTICATED: 'Нужно войти заново: сессия закончилась',
  FORBIDDEN: 'Недостаточно прав для этого действия',
  NOT_FOUND: 'Объект не найден',
  VALIDATION_FAILED: 'Данные заполнены неверно',
  REVISION_CONFLICT: 'Объект изменился, пока вы работали. Обновите страницу и повторите',
  CONFIRMATION_MISMATCH: 'Введённое значение не совпадает',
  NO_VERIFIED_BACKUP: 'Нет проверенной резервной копии',
  INVALID_STATE_TRANSITION: 'Операция недоступна в текущем состоянии',
  ENGINE_OPERATION_UNSUPPORTED: 'Платформа сайта не поддерживает эту операцию',
  RATE_LIMITED: 'Слишком много попыток. Подождите и повторите',
  INTERNAL_ERROR: 'Внутренняя ошибка сервера',
  PROVIDER_UNAVAILABLE: 'Внешний сервис недоступен',
  SITE_SLUG_TAKEN: 'Сайт с таким идентификатором уже есть',
  DOMAIN_ALREADY_BOUND: 'Этот домен уже привязан к другому сайту',
  IDEMPOTENCY_KEY_REUSED: 'Такой запрос уже отправлялся с другими данными',
};

/**
 * Why a job or a site failed, in the operator's words.
 *
 * A separate table from the request errors above: these codes come back from
 * the worker and the node, hours later and out of context, and they have to
 * say what went wrong with the site rather than with the click.
 */
const FAILURES: Record<string, string> = {
  NO_NODE_WITH_CAPACITY: 'Ни один сервер не может принять сайт',
  NODE_NOT_SCHEDULABLE: 'Выбранный сервер сейчас не принимает сайты',
  DOMAIN_ALREADY_BOUND: 'Домен уже привязан к другому сайту',
  SITE_SLUG_TAKEN: 'Сайт с таким идентификатором уже есть',
  PROVIDER_UNAVAILABLE: 'Сервер или внешний сервис не ответил',
  PROVIDER_TIMEOUT: 'Сервер не успел выполнить операцию',
  PROVIDER_REJECTED: 'Сервер отказался выполнить операцию',
  REVISION_CONFLICT: 'Сайт был занят другой операцией',
  FORBIDDEN: 'У того, кто поставил задачу, больше нет на неё прав',
  ENGINE_OPERATION_UNSUPPORTED: 'Операция не поддерживается',
  NO_VERIFIED_BACKUP: 'Нет проверенной резервной копии',
  INVALID_STATE_TRANSITION: 'Операция недоступна в состоянии сайта',
  VALIDATION_FAILED: 'Задача содержит неверные данные',
  INTERNAL_ERROR: 'Внутренняя ошибка',
  ROLLED_BACK: 'Сайт не ответил после обновления — восстановлена прежняя версия',
};

export function failureText(code: string): string {
  return FAILURES[code] ?? code;
}

const PLACEMENT: Record<string, string> = {
  not_schedulable: 'не принимает новые сайты',
  heartbeat_stale: 'агент не отвечает',
  disk_critical: 'диск почти заполнен',
  site_limit_reached: 'достигнут предел числа сайтов',
  insufficient_memory: 'не хватает свободной памяти',
  insufficient_disk: 'не хватает места на диске',
  missing_label: 'не подходит по меткам',
  anti_affinity: 'исключён правилом размещения',
};

const OPERATIONS: Record<string, string> = {
  'site.provision': 'создание базы данных',
  'site.install': 'установка WordPress',
  'site.deploy': 'запуск контейнера',
  'site.restart': 'перезапуск',
  'site.backup': 'резервная копия',
  'site.restore': 'восстановление',
  'site.destroy': 'удаление',
  'site.health_check': 'проверка доступности',
  'site.stop': 'остановка',
  'site.maintenance_on': 'включение обслуживания',
  'site.maintenance_off': 'снятие обслуживания',
  'site.clone_prepare': 'подготовка клона',
  'site.restore_drill': 'проверка восстановления',
};

/**
 * The details of a failure, as lines an operator can act on. Only the shapes
 * the backend is known to send are read; anything else is left out rather
 * than dumped as JSON, because a wall of braces is not an explanation.
 */
export function failureDetails(
  details: Record<string, unknown> | null,
  nodeName: (id: string) => string = (id) => id.slice(0, 8),
): string[] {
  if (!details) return [];
  const lines: string[] = [];

  const rejected = details['rejected'];
  if (Array.isArray(rejected)) {
    if (rejected.length === 0) lines.push('Нет ни одного подключённого сервера');
    for (const item of rejected as { nodeId?: unknown; reason?: unknown }[]) {
      const reason = String(item.reason ?? '');
      lines.push(`${nodeName(String(item.nodeId ?? ''))}: ${PLACEMENT[reason] ?? reason}`);
    }
    if (typeof details['requiredMemoryMb'] === 'number') {
      lines.push(`Сайту нужно ${details['requiredMemoryMb']} МБ памяти и ${String(details['requiredDiskGb'] ?? '?')} ГБ диска`);
    }
  }

  if (typeof details['operation'] === 'string') {
    const operation = details['operation'];
    lines.push(`Шаг на сервере: ${OPERATIONS[operation] ?? operation}`);
  }
  if (typeof details['agentMessage'] === 'string' && details['agentMessage']) {
    lines.push(`Сервер сообщил: ${details['agentMessage']}`);
  }
  if (Array.isArray(details['taken'])) {
    lines.push(`Заняты: ${(details['taken'] as unknown[]).map(String).join(', ')}`);
  }

  return lines;
}

/** The stable code the server answered with; null for anything that was not an API answer. */
export function errorCode(error: unknown): string | null {
  return error instanceof ApiError ? error.code : null;
}

/** What the server said about the failure beyond its code; empty when nothing. */
export function errorDetails(error: unknown): Readonly<Record<string, unknown>> {
  return error instanceof ApiError ? error.details : {};
}

export function errorText(error: unknown, fallback = 'Не удалось выполнить операцию'): string {
  if (!(error instanceof ApiError)) return fallback;
  return MESSAGES[error.code] ?? error.message;
}

/**
 * Which form field the server blamed, when it said.
 *
 * Lets a form put the message next to the input that caused it instead of in a
 * general banner the operator has to match up themselves.
 */
export function errorField(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  const field = error.details.field;
  return typeof field === 'string' ? field : null;
}

/**
 * What an outside service (2d2s) answered, in its own words, when it was the
 * one that refused: they say why — no money, no rights — where the code only
 * says that it refused. Null for the panel's own refusals.
 */
export function providerSaid(error: unknown): string | null {
  if (!(error instanceof ApiError) || typeof error.details.provider !== 'string') return null;
  return error.message.replace(/^[^:]+:\s*/, '');
}

/** Why an operation could not go ahead, where the server says (`details.reason`). */
export function errorReason(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  const reason = error.details.reason;
  return typeof reason === 'string' ? reason : null;
}

/** The code and request id, for reporting a problem. Null when there is nothing
 *  worth quoting, so a caller can skip the line entirely. */
export function errorTrace(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  return error.requestId ? `${error.code} · запрос ${error.requestId}` : error.code;
}
